/**
 * BidLevelingModal — side-by-side bid comparison (leveling) table.
 *
 * Per the outline-first rule, the outline itself shows bids as expandable rows; the
 * tabular comparison view lives here, opened from the Bid Campaign ⋯ "Compare bids"
 * action (or the inline "Compare bids" link) via:
 *   CustomEvent('open-bid-leveling-modal', { detail: { campaignId } })
 *
 * Reads the campaign detail from ctx.bidCampaignDetailCache (populated when the campaign
 * is expanded); falls back to getCampaignDetail if not cached.
 */

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { getCampaignDetail, bidAdjustment } from '../services/bidCampaigns.js'

function money(n) {
  if (n == null || n === '') return '—'
  return '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

const ROWS = [
  { label: 'Raw price',             get: b => money(b.price_raw) },
  { label: 'Allowance baseline',    get: b => money(b.allowance_baseline_delta), adj: true },
  { label: 'Alternate selection',   get: b => money(b.alternate_selection_delta), adj: true },
  { label: 'Excluded scope',        get: b => money(b.excluded_scope_delta), adj: true },
  { label: 'Bidder-included extra', get: b => money(b.bidder_included_extra_deduct), adj: true },
  { label: 'Tax + permits',         get: b => money(b.tax_permits_delta), adj: true },
  { label: 'GC + bonding',          get: b => money(b.gc_bonding_delta), adj: true },
  { label: 'Total adjustment',      get: b => money(bidAdjustment(b)), adj: true, strong: true },
  { label: 'Normalized price',      get: b => money(b.price_normalized), strong: true },
  { label: 'Method',                get: b => b.contracting_method || '—' },
  { label: 'Division 01',           get: b => money(b.division_01) },
  { label: 'Division 02',           get: b => money(b.division_02) },
  { label: 'Division 03',           get: b => money(b.division_03) },
  { label: 'Division 04',           get: b => money(b.division_04) },
  { label: 'Division 05',           get: b => money(b.division_05) },
]

export default function BidLevelingModal() {
  const [open,       setOpen]       = useState(false)
  const [campaignId, setCampaignId] = useState(null)
  const [detail,     setDetail]     = useState(null)

  useEffect(() => {
    function onOpen(e) {
      const cid = e.detail?.campaignId || null
      setCampaignId(cid)
      setOpen(true)
      const cached = cid ? ctx.bidCampaignDetailCache?.[cid] : null
      if (cached && !cached.error) {
        setDetail(cached)
      } else if (cid) {
        setDetail(null)
        getCampaignDetail(cid).then(d => setDetail(d)).catch(() => setDetail({ error: true }))
      }
    }
    window.addEventListener('open-bid-leveling-modal', onOpen)
    return () => window.removeEventListener('open-bid-leveling-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const bids = detail && !detail.error ? [...(detail.bids || [])].sort((a, b) => Number(a.price_normalized) - Number(b.price_normalized)) : []
  const lowId = bids.length ? bids[0].id : null
  const awardedId = detail?.contract?.winning_bid_id || null

  const th = { padding: '6px 10px', textAlign: 'right', fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', borderBottom: '0.5px solid var(--aj-border)', whiteSpace: 'nowrap' }
  const labelCell = { padding: '5px 10px', textAlign: 'left', fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--aj-bg-elevated)' }
  const valCell = { padding: '5px 10px', textAlign: 'right', fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text)', whiteSpace: 'nowrap' }

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div data-modal-id="BidLevelingModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', width: 'min(900px, 100%)', maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', flex: 1 }}>
            Bid Leveling
          </div>
          <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 16, lineHeight: 1, padding: 4 }}>×</button>
        </div>
        <div style={{ overflow: 'auto', padding: '4px 0 12px' }}>
          {!detail && <div style={{ padding: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>}
          {detail?.error && <div style={{ padding: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>Could not load campaign.</div>}
          {detail && !detail.error && bids.length === 0 && <div style={{ padding: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>No bids received yet.</div>}
          {detail && !detail.error && bids.length > 0 && (
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left', position: 'sticky', left: 0, background: 'var(--aj-bg-elevated)' }}></th>
                  {bids.map(b => (
                    <th key={b.id} style={th}>
                      {b.bidder_name || 'Bidder'}
                      {b.id === lowId && <span style={{ marginLeft: 5, color: '#2563eb' }}>LOW</span>}
                      {b.id === awardedId && <span style={{ marginLeft: 5, color: '#1D9E75' }}>AWARDED</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(r => (
                  <tr key={r.label} style={{ borderBottom: '0.5px solid var(--aj-border)' }}>
                    <td style={labelCell}>{r.label}</td>
                    {bids.map(b => (
                      <td key={b.id} style={{ ...valCell, fontWeight: r.strong ? 600 : 400, color: r.adj ? '#d97706' : (b.id === awardedId ? '#1D9E75' : b.id === lowId ? '#2563eb' : 'var(--aj-text)') }}>
                        {r.get(b)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
